import { useState, useRef, useEffect } from 'react';
import { Storage } from '../storage';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import NotificationBell from '../components/NotificationBell';
import MobileMenu from '../components/MobileMenu';
import { useConsent } from '../contexts/ConsentContext';

// ─── PROFILE PAGE ─────────────────────────────────────────────────────────────
//
//  Lets the signed-in user view and edit their profile:
//    - Profile picture (Supabase Storage URL, circular)
//    - Display name
//    - Bio
//    - Email (read-only — used as login identifier)
//    - Member-since date
//
//  Props:
//    user   {object}    the current user object
//    onSave {function}  called with the updated user object
//    onBack {function}  navigates back to the dashboard
//

const getInitials = (name) => {
  const parts = (name || '').trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (name || '??').slice(0, 2).toUpperCase();
};

const ProfilePage = ({ user, onSave, onBack, onFeed, onDeleteAccount, theme, onThemeToggle, canInstall, isStandalone, onInstall, onNotificationClick, onViewProfile, onViewTimeline }) => {
  const { consent, updateConsent, resetConsent } = useConsent();
  const [consentAnalytics,   setConsentAnalytics]   = useState(consent.analytics);
  const [consentPreferences, setConsentPreferences] = useState(consent.preferences);
  const [consentSaved,       setConsentSaved]       = useState(false);

  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleSaveConsent = () => {
    updateConsent({ analytics: consentAnalytics, preferences: consentPreferences });
    setConsentSaved(true);
    setTimeout(() => setConsentSaved(false), 2000);
  };

  const handleResetConsent = () => {
    resetConsent();
    setConsentAnalytics(false);
    setConsentPreferences(false);
  };

  const [name,             setName]             = useState(user.name   || '');
  const [bio,              setBio]              = useState(user.bio    || '');
  const [avatar,           setAvatar]           = useState(user.avatar || null);
  const [saved,            setSaved]            = useState(false);
  const [error,            setError]            = useState('');
  const [isDirty,          setIsDirty]          = useState(false);
  const [showLeaveWarning, setShowLeaveWarning] = useState(false);

  // ── Email change modal state ───────────────────────────────────────────────
  const [showEmailModal,    setShowEmailModal]    = useState(false);
  const [newEmail,          setNewEmail]          = useState('');
  const [emailPassword,     setEmailPassword]     = useState('');
  const [emailModalError,   setEmailModalError]   = useState('');
  const [emailModalLoading, setEmailModalLoading] = useState(false);
  const [emailModalSuccess, setEmailModalSuccess] = useState(false);

  // ── Delete account modal state ────────────────────────────────────────────
  const [showDeleteModal,  setShowDeleteModal]  = useState(false);
  const [deletePassword,   setDeletePassword]   = useState('');
  const [deleteError,      setDeleteError]      = useState('');
  const [deleteLoading,    setDeleteLoading]    = useState(false);

  // ── Active sessions state ─────────────────────────────────────────────────
  const [sessions,          setSessions]          = useState([]);
  const [sessionsLoading,   setSessionsLoading]   = useState(true);
  const [revokeLoadingId,   setRevokeLoadingId]   = useState(null);
  const [showRevokeAll,     setShowRevokeAll]     = useState(false);
  const [revokeAllPassword, setRevokeAllPassword] = useState('');
  const [revokeAllError,    setRevokeAllError]    = useState('');
  const [revokeAllLoading,  setRevokeAllLoading]  = useState(false);

  // ── Blocked users state ───────────────────────────────────────────────────
  const [blockedUsers,        setBlockedUsers]        = useState([]);
  const [blockedUsersLoading, setBlockedUsersLoading] = useState(true);
  const [unblockingId,        setUnblockingId]        = useState(null);

  // ── Profile section tab ───────────────────────────────────────────────────
  const [profileTab, setProfileTab] = useState('profile'); // 'profile' | 'bookmarks'

  // ── Bookmarks state ───────────────────────────────────────────────────────
  const [bookmarks,        setBookmarks]        = useState([]);
  const [bookmarksLoading, setBookmarksLoading] = useState(false);
  const [bookmarksLoaded,  setBookmarksLoaded]  = useState(false);
  const [editingBmNote,    setEditingBmNote]    = useState(null); // timeline_id
  const [bmNoteText,       setBmNoteText]       = useState('');
  const [bmToast,          setBmToast]          = useState('');
  const bmToastTimerRef = useRef(null);


  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const url = await Storage.uploadImage(file, 'avatar');
      setAvatar(url);
      setIsDirty(true);
    } catch (err) {
      setError('Photo upload failed. Please try again.');
    }
  };

  const handleSave = () => {
    if (!name.trim()) { setError('Name cannot be empty.'); return; }
    setError('');
    const updated = { ...user, name: name.trim(), bio: bio.trim(), avatar: avatar || null };
    onSave(updated);
    setIsDirty(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleBack = () => {
    if (isDirty) { setShowLeaveWarning(true); return; }
    onBack();
  };

  const closeEmailModal = () => {
    setShowEmailModal(false);
    setNewEmail('');
    setEmailPassword('');
    setEmailModalError('');
    setEmailModalLoading(false);
    setEmailModalSuccess(false);
  };

  const handleEmailChange = async () => {
    setEmailModalError('');
    if (!newEmail.trim())  { setEmailModalError('Please enter a new email address.'); return; }
    if (!emailPassword)    { setEmailModalError('Please enter your current password.'); return; }
    setEmailModalLoading(true);
    try {
      await Storage.requestEmailChange(user.id, newEmail.trim(), emailPassword);
      setEmailModalSuccess(true);
    } catch (err) {
      setEmailModalError(err.message);
    } finally {
      setEmailModalLoading(false);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletePassword('');
    setDeleteError('');
    setDeleteLoading(false);
  };

  const handleExportData = async () => {
    try {
      await Storage.exportAccountData(user.id);
    } catch (err) {
      alert(`Export failed: ${err.message}`);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteError('');
    if (!deletePassword) { setDeleteError('Please enter your password.'); return; }
    setDeleteLoading(true);
    try {
      await Storage.deleteAccount(user.id, deletePassword);
      onDeleteAccount();
    } catch (err) {
      setDeleteError(err.message);
      setDeleteLoading(false);
    }
  };

  // ── Active sessions ───────────────────────────────────────────────────────

  useEffect(() => {
    Storage.getSessions()
      .then(setSessions)
      .catch(() => setSessions([]))
      .finally(() => setSessionsLoading(false));
  }, []);

  useEffect(() => {
    Storage.getBlockedUsers({ page: 1, limit: 50 })
      .then(setBlockedUsers)
      .catch(() => setBlockedUsers([]))
      .finally(() => setBlockedUsersLoading(false));
  }, []);

  useEffect(() => {
    if (profileTab !== 'bookmarks' || bookmarksLoaded) return;
    setBookmarksLoading(true);
    Storage.getBookmarks({ page: 1, limit: 50 })
      .then(data => { setBookmarks(data.bookmarks ?? []); setBookmarksLoaded(true); })
      .catch(() => setBookmarks([]))
      .finally(() => setBookmarksLoading(false));
  }, [profileTab]);

  const handleUnblock = async (userId) => {
    setUnblockingId(userId);
    try {
      await Storage.unblockUser(userId);
      setBlockedUsers(prev => prev.filter(u => u.id !== userId));
    } catch {}
    setUnblockingId(null);
  };

  const handleRevokeSession = async (sessionId) => {
    setRevokeLoadingId(sessionId);
    try {
      await Storage.revokeSession(sessionId);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
    } catch {}
    setRevokeLoadingId(null);
  };

  const handleRevokeAll = async () => {
    setRevokeAllError('');
    if (!revokeAllPassword) { setRevokeAllError('Please enter your password.'); return; }
    setRevokeAllLoading(true);
    try {
      await Storage.revokeOtherSessions(revokeAllPassword);
      setSessions(prev => prev.filter(s => s.isCurrent));
      setShowRevokeAll(false);
      setRevokeAllPassword('');
    } catch (err) {
      setRevokeAllError(err.message);
    }
    setRevokeAllLoading(false);
  };

  const handleSaveBmNote = async (timelineId) => {
    const trimmed = bmNoteText.trim();
    try {
      await Storage.updateBookmarkNote(timelineId, trimmed || null);
      setBookmarks(prev => prev.map(bm =>
        bm.tl.id === timelineId ? { ...bm, note: trimmed || null } : bm
      ));
    } catch {}
    setEditingBmNote(null);
  };

  const handleRemoveBookmark = async (timelineId) => {
    try {
      await Storage.removeBookmark(timelineId);
      setBookmarks(prev => prev.filter(bm => bm.tl.id !== timelineId));
      clearTimeout(bmToastTimerRef.current);
      setBmToast('Removed from bookmarks');
      bmToastTimerRef.current = setTimeout(() => setBmToast(''), 3000);
    } catch {}
  };

  // Minimal UA → human-readable label
  const parseUA = (ua) => {
    if (!ua) return 'Unknown device';
    if (/iPhone/i.test(ua))                        return 'iPhone';
    if (/iPad/i.test(ua))                          return 'iPad';
    if (/Android/i.test(ua) && /Mobile/i.test(ua)) return 'Android phone';
    if (/Android/i.test(ua))                       return 'Android tablet';
    if (/Macintosh/i.test(ua))                     return 'Mac';
    if (/Windows/i.test(ua))                       return 'Windows PC';
    if (/Linux/i.test(ua))                         return 'Linux';
    return 'Unknown device';
  };

  const fmtDate = (val) => val ? new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  const memberSince = user.createdAt
    ? new Date(user.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : 'Unknown';

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>

      {/* ── Nav ── */}
      <nav style={{
        background: 'var(--surface)', borderBottom: '1px solid var(--border)',
        padding: '0 24px', display: 'flex', alignItems: 'center', gap: 16, height: 64,
      }}>
        <button
          onClick={onFeed || onBack}
          style={{ display: 'flex', alignItems: 'center', gap: 0, background: 'none', border: 'none', padding: 0, cursor: 'pointer', flex: 1 }}
          aria-label="Go to feed"
        >
          <div style={{ fontSize: 22, marginRight: 4, filter: 'brightness(0) invert(1)' }} aria-hidden="true">⏱</div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--accent)', margin: 0 }}>Chronos</h1>
        </button>

        {/* Desktop nav */}
        {!isMobile && (<>
          <button
            className="btn-ghost btn-sm"
            onClick={onThemeToggle}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, padding: 0 }}
          >
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={15} />
          </button>
          <button className="btn-ghost btn-sm" onClick={handleBack} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="back" size={14} /> My Dashboard
          </button>
        </>)}

        {/* Mobile hamburger */}
        {isMobile && (
          <MobileMenu>
            {(close) => <>
              <button
                onClick={() => { handleBack(); close(); }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 16px', background: 'none', border: 'none', fontSize: 14, color: 'var(--text)', cursor: 'pointer', textAlign: 'left' }}
              >
                <Icon name="back" size={15} /> My Dashboard
              </button>
              {user?.id && onNotificationClick && (
                <div style={{ padding: '4px 16px' }}>
                  <NotificationBell userId={user.id} onViewTimeline={onNotificationClick} onViewProfile={onViewProfile} />
                </div>
              )}
              <button
                onClick={() => { onThemeToggle(); close(); }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 16px', background: 'none', border: 'none', fontSize: 14, color: 'var(--text)', cursor: 'pointer', textAlign: 'left' }}
              >
                <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={15} />
                {theme === 'dark' ? 'Light mode' : 'Dark mode'}
              </button>
            </>}
          </MobileMenu>
        )}
      </nav>

      {/* ── Content ── */}
      <main style={{ maxWidth: 640, margin: '0 auto', padding: '48px 24px' }}>

        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 28, marginBottom: 24 }}>My Profile</h2>

        {/* ── Tabs ── */}
        <div style={{ display: 'flex', marginBottom: 32, borderBottom: '1px solid var(--border)' }}>
          {[
            ['profile',   'user',     'Profile'],
            ['bookmarks', 'bookmark', 'Bookmarks'],
          ].map(([tab, icon, label]) => (
            <button
              key={tab}
              type="button"
              onClick={() => setProfileTab(tab)}
              style={{
                background: 'none', border: 'none', padding: '10px 18px',
                fontSize: 14, fontWeight: 500, cursor: 'pointer',
                color: profileTab === tab ? 'var(--accent)' : 'var(--text3)',
                borderBottom: `2px solid ${profileTab === tab ? 'var(--accent)' : 'transparent'}`,
                display: 'flex', alignItems: 'center', gap: 6, marginBottom: -1,
              }}
            >
              <Icon name={icon} size={14} /> {label}
            </button>
          ))}
        </div>

        {profileTab === 'profile' ? (<>

        {/* ── Avatar ── */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 40 }}>
          <div style={{ position: 'relative', marginBottom: 14 }}>
            {avatar ? (
              <img
                src={avatar}
                alt={`${name}'s profile picture`}
                style={{
                  width: 120, height: 120, borderRadius: '50%',
                  objectFit: 'cover',
                  border: `3px solid var(--accent)`,
                  display: 'block',
                }}
              />
            ) : (
              <div
                aria-label={`Profile initials: ${getInitials(name)}`}
                style={{
                  width: 120, height: 120, borderRadius: '50%',
                  background: `linear-gradient(135deg, var(--accent), var(--accent3))`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 40, fontWeight: 700, color: 'var(--on-accent)',
                  fontFamily: 'var(--font-display)',
                  border: `3px solid var(--accent)`,
                  userSelect: 'none',
                }}
              >
                {getInitials(name)}
              </div>
            )}

            {/* Camera badge — file input overlaid directly for mobile compatibility */}
            <div style={{ position: 'absolute', bottom: 4, right: 4 }}>
              <div
                aria-label="Change profile picture"
                style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: 'var(--accent)', border: '2px solid var(--bg)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--on-accent)',
                }}
              >
                <Icon name="image" size={14} />
              </div>
              <input
                type="file"
                accept="image/*"
                aria-label="Change profile picture"
                onChange={handleAvatarChange}
                style={{
                  position: 'absolute', inset: 0,
                  width: '100%', height: '100%',
                  opacity: 0, cursor: 'pointer', borderRadius: '50%',
                }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                className="btn-ghost btn-sm"
                style={{ display: 'flex', alignItems: 'center', gap: 6, pointerEvents: 'none' }}
              >
                <Icon name="image" size={13} />
                {avatar ? 'Change Photo' : 'Upload Photo'}
              </button>
              <input
                type="file"
                accept="image/*"
                aria-label={avatar ? 'Change photo' : 'Upload photo'}
                onChange={handleAvatarChange}
                style={{
                  position: 'absolute', inset: 0,
                  width: '100%', height: '100%',
                  opacity: 0, cursor: 'pointer',
                }}
              />
            </div>
            {avatar && (
              <button
                type="button"
                className="btn-ghost btn-sm"
                onClick={() => { setAvatar(null); setIsDirty(true); }}
                style={{ color: 'var(--danger)', borderColor: 'rgba(201,88,88,0.3)' }}
              >
                Remove
              </button>
            )}
          </div>
        </div>

        {/* ── Fields ── */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 32, display: 'flex', flexDirection: 'column', gap: 20 }}>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <label htmlFor="profile-name" style={{ margin: 0 }}>Display Name</label>
              <span style={{ fontSize: 11, color: name.length >= 30 ? 'var(--danger)' : 'var(--text3)' }}>
                {name.length} / 30
              </span>
            </div>
            <input
              id="profile-name"
              value={name}
              onChange={e => { setName(e.target.value.slice(0, 30)); setIsDirty(true); }}
              placeholder="Your name"
              aria-required="true"
              maxLength={30}
            />
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <label htmlFor="profile-email" style={{ margin: 0 }}>Email</label>
              <button
                type="button"
                className="btn-ghost btn-sm"
                onClick={() => setShowEmailModal(true)}
                style={{ fontSize: 11, padding: '3px 8px' }}
              >
                Change
              </button>
            </div>
            <input
              id="profile-email"
              type="email"
              value={user.email}
              readOnly
              aria-readonly="true"
              maxLength={100}
              style={{ opacity: 0.6, cursor: 'not-allowed' }}
            />
            {user.pendingEmail && (
              <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>
                Verification sent to{' '}
                <strong style={{ color: 'var(--text2)' }}>{user.pendingEmail}</strong>
                {' '}— check your inbox to confirm the change.
              </p>
            )}
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <label htmlFor="profile-bio" style={{ margin: 0 }}>
                About Me <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(optional)</span>
              </label>
              <span style={{ fontSize: 11, color: bio.length >= 1000 ? 'var(--danger)' : bio.length >= 900 ? '#c9a96e' : 'var(--text3)' }}>
                {bio.length} / 1,000
              </span>
            </div>
            <textarea
              id="profile-bio"
              value={bio}
              onChange={e => { setBio(e.target.value.slice(0, 1000)); setIsDirty(true); }}
              rows={4}
              placeholder="Tell a little about yourself…"
              maxLength={1000}
              style={{ resize: 'vertical', borderColor: bio.length >= 1000 ? 'var(--danger)' : undefined }}
            />
          </div>

          <div style={{ paddingTop: 4, borderTop: '1px solid var(--border)', fontSize: 13, color: 'var(--text3)' }}>
            Member since {memberSince}
          </div>

          {error && (
            <p role="alert" aria-live="assertive" style={{ color: 'var(--danger)', fontSize: 13, margin: 0 }}>
              {error}
            </p>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              className="btn-primary"
              onClick={handleSave}
              aria-label={saved ? 'Profile saved' : 'Save profile changes'}
              style={{ minWidth: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            >
              <Icon name="save" size={14} />
              {saved ? '✓ Saved' : 'Save Changes'}
            </button>
          </div>
        </div>
        {/* ── Preferences ── */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 32, marginTop: 24 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, marginBottom: 20 }}>Preferences</h3>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
            <div>
              <p style={{ fontWeight: 500, fontSize: 14, marginBottom: 4 }}>Appearance</p>
              <p style={{ fontSize: 13, color: 'var(--text3)' }}>Choose your preferred color scheme</p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['dark', 'moon', 'Dark'], ['light', 'sun', 'Light']].map(([val, icon, label]) => (
                <button
                  key={val}
                  type="button"
                  aria-pressed={theme === val}
                  onClick={() => theme !== val && onThemeToggle()}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '8px 14px', borderRadius: 'var(--radius)', fontSize: 13,
                    border: `1px solid ${theme === val ? 'var(--accent)' : 'var(--border2)'}`,
                    background: theme === val ? 'var(--accent)22' : 'transparent',
                    color: theme === val ? 'var(--accent)' : 'var(--text2)',
                    cursor: theme === val ? 'default' : 'pointer',
                    fontWeight: theme === val ? 600 : 400,
                  }}
                >
                  <Icon name={icon} size={13} /> {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Install App ── */}
        {(canInstall || isStandalone) && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 32, marginTop: 24 }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, marginBottom: 8 }}>Mobile App</h3>
            <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 20, lineHeight: 1.6 }}>
              {isStandalone
                ? 'Chronos is installed on this device.'
                : 'Install Chronos on your device for a faster, native experience — works offline too.'}
            </p>
            {isStandalone ? (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 'var(--radius)', background: 'var(--success)18', border: '1px solid var(--success)40', color: 'var(--success)', fontSize: 13, fontWeight: 600 }}>
                <Icon name="check" size={14} /> Installed
              </div>
            ) : (
              <button
                onClick={onInstall}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  background: 'var(--accent)', color: '#1a1408',
                  border: 'none', borderRadius: 'var(--radius)',
                  padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                }}
              >
                <Icon name="phone" size={15} /> Install App
              </button>
            )}
          </div>
        )}

        {/* ── Active Sessions ── */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 32, marginTop: 24 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, marginBottom: 6 }}>Active Sessions</h3>
          <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 20, lineHeight: 1.6 }}>
            Devices and browsers that are currently signed in to your account.
          </p>

          {sessionsLoading ? (
            <p style={{ fontSize: 13, color: 'var(--text3)' }}>Loading…</p>
          ) : sessions.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text3)' }}>No active sessions found.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {sessions.map(s => (
                <div key={s.id} style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  background: 'var(--bg2)', borderRadius: 'var(--radius)',
                  padding: '12px 16px',
                  border: s.isCurrent ? '1px solid var(--accent)44' : '1px solid var(--border)',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{parseUA(s.userAgent)}</span>
                      {s.isCurrent && (
                        <span style={{ fontSize: 11, background: 'var(--accent)22', color: 'var(--accent)', borderRadius: 4, padding: '2px 7px', fontWeight: 600 }}>
                          This device
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                      {s.ipPrefix && <span>{s.ipPrefix} · </span>}
                      <span>Signed in {fmtDate(s.createdAt)}</span>
                      {s.expiresAt && <span> · Expires {fmtDate(s.expiresAt)}</span>}
                    </div>
                  </div>
                  {!s.isCurrent && (
                    <button
                      type="button"
                      className="btn-ghost btn-sm"
                      onClick={() => handleRevokeSession(s.id)}
                      disabled={revokeLoadingId === s.id}
                      style={{ color: 'var(--danger)', borderColor: 'rgba(201,88,88,0.3)', flexShrink: 0, opacity: revokeLoadingId === s.id ? 0.5 : 1 }}
                    >
                      {revokeLoadingId === s.id ? 'Revoking…' : 'Revoke'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {sessions.filter(s => !s.isCurrent).length > 0 && (
            <div style={{ marginTop: 16 }}>
              <button
                type="button"
                className="btn-ghost btn-sm"
                onClick={() => { setShowRevokeAll(true); setRevokeAllPassword(''); setRevokeAllError(''); }}
                style={{ color: 'var(--danger)', borderColor: 'rgba(201,88,88,0.3)' }}
              >
                Sign Out All Other Devices
              </button>
            </div>
          )}
        </div>

        {/* ── Blocked Users ── */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 32, marginTop: 24 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, marginBottom: 6 }}>Blocked Users</h3>
          <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 20, lineHeight: 1.6 }}>
            Blocked users cannot follow you, comment on your timelines, or see your public profile.
          </p>

          {blockedUsersLoading ? (
            <p style={{ fontSize: 13, color: 'var(--text3)' }}>Loading…</p>
          ) : blockedUsers.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text3)' }}>You haven't blocked anyone.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {blockedUsers.map(u => (
                <div key={u.id} style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  background: 'var(--bg2)', borderRadius: 'var(--radius)',
                  padding: '12px 16px', border: '1px solid var(--border)',
                }}>
                  {u.avatar ? (
                    <img src={u.avatar} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                  ) : (
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                      background: 'linear-gradient(135deg, var(--accent), var(--accent3))',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 700, color: 'var(--on-accent)',
                    }}>
                      {(u.name || '?').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase()}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>{u.name}</p>
                    <p style={{ fontSize: 12, color: 'var(--text3)' }}>Blocked {fmtDate(u.blockedAt)}</p>
                  </div>
                  <button
                    type="button"
                    className="btn-ghost btn-sm"
                    onClick={() => handleUnblock(u.id)}
                    disabled={unblockingId === u.id}
                    style={{ flexShrink: 0, opacity: unblockingId === u.id ? 0.5 : 1 }}
                  >
                    {unblockingId === u.id ? 'Unblocking…' : 'Unblock'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Cookie Preferences ── */}
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-xl)', padding: 32, marginTop: 24,
        }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, marginBottom: 6 }}>
            Cookie Preferences
          </h3>
          <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 20, lineHeight: 1.6 }}>
            Control what Chronos stores on this device.{' '}
            <a href="/privacy" style={{ color: 'var(--accent)' }}>Privacy Policy</a>
          </p>

          {[
            { id: 'necessary', label: 'Necessary', desc: 'Session auth and theme preference. Always required.', required: true, value: true },
            { id: 'analytics', label: 'Analytics', desc: 'Sentry session replays and performance monitoring.', required: false, value: consentAnalytics, setter: setConsentAnalytics },
            { id: 'preferences', label: 'Preferences', desc: 'Feed tab, editor tour state, font size, and other UI memory.', required: false, value: consentPreferences, setter: setConsentPreferences },
          ].map(cat => (
            <div key={cat.id} style={{ display: 'flex', gap: 16, alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{cat.label}{cat.required && <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 6 }}>Required</span>}</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{cat.desc}</div>
              </div>
              <button
                role="switch"
                aria-checked={cat.value}
                disabled={cat.required}
                onClick={() => !cat.required && cat.setter(!cat.value)}
                style={{
                  position: 'relative', width: 40, height: 22, borderRadius: 11,
                  border: 'none', cursor: cat.required ? 'not-allowed' : 'pointer',
                  background: cat.value ? 'var(--accent)' : 'var(--border2)',
                  transition: 'background 0.2s', flexShrink: 0, outline: 'none', padding: 0,
                }}
              >
                <span style={{ position: 'absolute', top: 3, left: cat.value ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', display: 'block' }} />
              </button>
            </div>
          ))}

          <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
            <button className="btn-ghost btn-sm" onClick={handleResetConsent}>Reset to defaults</button>
            <button className="btn-primary btn-sm" onClick={handleSaveConsent}>
              {consentSaved ? 'Saved ✓' : 'Save Preferences'}
            </button>
          </div>
        </div>

        {/* ── Data Export ── */}
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-xl)',
          padding: 32,
          marginTop: 24,
        }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, marginBottom: 8 }}>
            Your Data
          </h3>
          <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 12, lineHeight: 1.6 }}>
            Download a copy of all your Chronos data including your profile, timelines, comments, and activity. You will receive a confirmation email when the export is downloaded.
          </p>
          <button className="btn-ghost btn-sm" onClick={handleExportData} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name="download" size={13} /> Download My Data
          </button>
        </div>

        {/* ── Danger Zone ── */}
        <div style={{
          background: 'var(--surface)',
          border: '1px solid rgba(201,88,88,0.35)',
          borderRadius: 'var(--radius-xl)',
          padding: 32,
          marginTop: 24,
        }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: 'var(--danger)', marginBottom: 8 }}>
            Danger Zone
          </h3>
          <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 20, lineHeight: 1.6 }}>
            Permanently delete your account and all associated data. This cannot be undone.
          </p>
          <button
            type="button"
            className="btn-danger"
            onClick={() => setShowDeleteModal(true)}
          >
            Delete Account
          </button>
        </div>

        </>) : profileTab === 'bookmarks' ? (

        /* ── Bookmarks tab ── */
        <div>
          {bookmarksLoading ? (
            <p style={{ fontSize: 14, color: 'var(--text3)', textAlign: 'center', padding: '64px 0' }}>Loading bookmarks…</p>
          ) : bookmarks.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '64px 24px', color: 'var(--text3)' }}>
              <div style={{ opacity: 0.3, display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                <Icon name="bookmark" size={40} />
              </div>
              <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 8, color: 'var(--text2)' }}>No bookmarks yet</p>
              <p style={{ fontSize: 13 }}>Tap the bookmark icon on any timeline to save it here.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
              {bookmarks.map(bm => {
                const accent    = bm.tl.colors?.accent || 'var(--accent)';
                const isEditing = editingBmNote === bm.tl.id;
                return (
                  <div key={bm.tl.id} style={{
                    background: 'var(--surface)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-xl)', overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                  }}>
                    {/* Color / image strip */}
                    <div style={{
                      height: 80, flexShrink: 0, position: 'relative',
                      background: bm.tl.bgImage
                        ? `url(${bm.tl.bgImage}) center/cover no-repeat`
                        : accent,
                    }}>
                      <button
                        type="button"
                        onClick={() => handleRemoveBookmark(bm.tl.id)}
                        aria-label="Remove bookmark"
                        style={{
                          position: 'absolute', top: 8, right: 8,
                          width: 26, height: 26, borderRadius: '50%',
                          background: 'rgba(0,0,0,0.45)', border: 'none',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          cursor: 'pointer', color: '#fff',
                        }}
                      >
                        <Icon name="close" size={12} />
                      </button>
                    </div>

                    {/* Card body */}
                    <div style={{ padding: '14px 16px', flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', lineHeight: 1.35, margin: 0 }}>
                        {bm.tl.title}
                      </p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, color: 'var(--text3)' }}>by {bm.author.name}</span>
                        {bm.tl.category && (
                          <span style={{
                            fontSize: 11, padding: '2px 7px', borderRadius: 4,
                            background: 'var(--bg2)', color: 'var(--text3)', fontWeight: 500,
                          }}>
                            {bm.tl.category}
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize: 11, color: 'var(--text3)', margin: 0 }}>
                        Bookmarked {fmtDate(bm.bookmarkedAt)}
                      </p>

                      {/* Note area */}
                      {isEditing ? (
                        <input
                          value={bmNoteText}
                          onChange={e => setBmNoteText(e.target.value.slice(0, 200))}
                          onBlur={() => handleSaveBmNote(bm.tl.id)}
                          onKeyDown={e => {
                            if (e.key === 'Enter')  handleSaveBmNote(bm.tl.id);
                            if (e.key === 'Escape') setEditingBmNote(null);
                          }}
                          maxLength={200}
                          autoFocus
                          placeholder="Add a personal note…"
                          style={{ fontSize: 12, marginTop: 4 }}
                        />
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: 2 }}>
                          {bm.note ? (
                            <span style={{ fontSize: 12, color: 'var(--text2)', fontStyle: 'italic', flex: 1, lineHeight: 1.4 }}>
                              {bm.note}
                            </span>
                          ) : (
                            <span style={{ fontSize: 12, color: 'var(--text3)', flex: 1 }}>No note</span>
                          )}
                          <button
                            type="button"
                            onClick={() => { setEditingBmNote(bm.tl.id); setBmNoteText(bm.note || ''); }}
                            aria-label="Edit note"
                            style={{
                              background: 'none', border: 'none', padding: 4,
                              cursor: 'pointer', color: 'var(--text3)', flexShrink: 0,
                              display: 'flex', alignItems: 'center',
                            }}
                          >
                            <Icon name="pencil" size={12} />
                          </button>
                        </div>
                      )}

                      {onViewTimeline && (
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={() => onViewTimeline(bm.tl, bm.author)}
                          style={{ marginTop: 8, fontSize: 13, padding: '8px 0', width: '100%' }}
                        >
                          View Timeline
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        ) : null}

        {/* ── Bookmark removed toast ── */}
        {bmToast && (
          <div role="status" style={{
            position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
            background: 'var(--text)', color: 'var(--bg)', padding: '10px 20px',
            borderRadius: 'var(--radius)', fontSize: 13, fontWeight: 500,
            pointerEvents: 'none', zIndex: 9999,
          }}>
            {bmToast}
          </div>
        )}

      </main>

      {/* ── Unsaved-changes warning ── */}
      <Modal open={showLeaveWarning} onClose={() => setShowLeaveWarning(false)} title="Unsaved Changes" width={400}>
        <p style={{ color: 'var(--text2)', marginBottom: 24 }}>
          You have unsaved changes. If you leave now your changes will be lost.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button className="btn-ghost" onClick={() => setShowLeaveWarning(false)}>Keep Editing</button>
          <button className="btn-danger" onClick={() => { setShowLeaveWarning(false); onBack(); }}>Leave Without Saving</button>
        </div>
      </Modal>

      {/* ── Change email modal ── */}
      <Modal open={showEmailModal} onClose={closeEmailModal} title="Change Email Address" width={440}>

        {emailModalSuccess ? (
          <div>
            <p style={{ color: 'var(--text2)', fontSize: 14, marginBottom: 20 }}>
              A verification link has been sent to{' '}
              <strong style={{ color: 'var(--text)' }}>{newEmail}</strong>.
              Click the link in that email to confirm the change. Your current
              email address will stay active until then.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn-primary" onClick={closeEmailModal}>Done</button>
            </div>
          </div>
        ) : (
          <div>
            <p style={{ color: 'var(--text2)', fontSize: 14, marginBottom: 20 }}>
              Enter a new email address and your current password to confirm the
              change. A verification link will be sent to the new address.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: emailModalError ? 8 : 20 }}>
              <div>
                <label htmlFor="email-modal-new">New Email Address</label>
                <input
                  id="email-modal-new"
                  type="email"
                  value={newEmail}
                  onChange={e => { setNewEmail(e.target.value); setEmailModalError(''); }}
                  placeholder="you@example.com"
                  autoComplete="email"
                  autoFocus
                />
              </div>
              <div>
                <label htmlFor="email-modal-password">Current Password</label>
                <input
                  id="email-modal-password"
                  type="password"
                  value={emailPassword}
                  onChange={e => { setEmailPassword(e.target.value); setEmailModalError(''); }}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  onKeyDown={e => e.key === 'Enter' && handleEmailChange()}
                />
              </div>
            </div>
            {emailModalError && (
              <p role="alert" aria-live="assertive" style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 16 }}>
                {emailModalError}
              </p>
            )}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn-ghost" onClick={closeEmailModal} disabled={emailModalLoading}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleEmailChange}
                disabled={emailModalLoading}
                style={{ opacity: emailModalLoading ? 0.7 : 1 }}
              >
                {emailModalLoading ? 'Sending…' : 'Send Verification'}
              </button>
            </div>
          </div>
        )}
      </Modal>
      {/* ── Revoke all other sessions modal ── */}
      <Modal open={showRevokeAll} onClose={() => setShowRevokeAll(false)} title="Sign Out All Other Devices" width={420}>
        <p style={{ color: 'var(--text2)', fontSize: 14, marginBottom: 20, lineHeight: 1.6 }}>
          All sessions except your current one will be immediately invalidated. Enter your password to confirm.
        </p>
        <div style={{ marginBottom: revokeAllError ? 8 : 20 }}>
          <label htmlFor="revoke-all-password">Current Password</label>
          <input
            id="revoke-all-password"
            type="password"
            value={revokeAllPassword}
            onChange={e => { setRevokeAllPassword(e.target.value); setRevokeAllError(''); }}
            placeholder="••••••••"
            autoComplete="current-password"
            onKeyDown={e => e.key === 'Enter' && !revokeAllLoading && handleRevokeAll()}
            autoFocus
          />
        </div>
        {revokeAllError && (
          <p role="alert" aria-live="assertive" style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 16 }}>
            {revokeAllError}
          </p>
        )}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button className="btn-ghost" onClick={() => setShowRevokeAll(false)} disabled={revokeAllLoading}>Cancel</button>
          <button
            className="btn-danger"
            onClick={handleRevokeAll}
            disabled={revokeAllLoading || !revokeAllPassword}
            style={{ opacity: revokeAllLoading || !revokeAllPassword ? 0.5 : 1 }}
          >
            {revokeAllLoading ? 'Signing out…' : 'Sign Out All Other Devices'}
          </button>
        </div>
      </Modal>

      {/* ── Delete account modal ── */}
      <Modal open={showDeleteModal} onClose={closeDeleteModal} title="Delete Account" width={460}>
        <div>
          <p style={{ color: 'var(--text2)', fontSize: 14, marginBottom: 14, lineHeight: 1.6 }}>
            <strong style={{ color: 'var(--danger)' }}>This action is permanent and cannot be undone.</strong>
            {' '}Everything listed below will be immediately and irrevocably deleted:
          </p>
          <ul style={{ color: 'var(--text3)', fontSize: 13, lineHeight: 1.9, marginBottom: 20, paddingLeft: 20 }}>
            <li>Your profile, display name, bio, and avatar</li>
            <li>All timelines and their events</li>
            <li>All comments and replies you have written</li>
            <li>All likes you have given</li>
            <li>All uploaded images (avatars, backgrounds, event photos)</li>
          </ul>
          <div style={{ marginBottom: deleteError ? 8 : 20 }}>
            <label htmlFor="delete-account-password">Enter your password to confirm</label>
            <input
              id="delete-account-password"
              type="password"
              value={deletePassword}
              onChange={e => { setDeletePassword(e.target.value); setDeleteError(''); }}
              placeholder="••••••••"
              autoComplete="current-password"
              onKeyDown={e => e.key === 'Enter' && deletePassword && !deleteLoading && handleDeleteAccount()}
            />
          </div>
          {deleteError && (
            <p role="alert" aria-live="assertive" style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 16 }}>
              {deleteError}
            </p>
          )}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button className="btn-ghost" onClick={closeDeleteModal} disabled={deleteLoading}>
              Cancel
            </button>
            <button
              className="btn-danger"
              onClick={handleDeleteAccount}
              disabled={deleteLoading || !deletePassword}
              style={{ opacity: deleteLoading || !deletePassword ? 0.5 : 1 }}
            >
              {deleteLoading ? 'Deleting…' : 'Delete My Account'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProfilePage;
